import { useEffect, useState } from "react";

import clsx from "clsx";
import moment, { type Moment } from "moment";
import { useIMask } from "react-imask";

import { TextInput } from "@shared/text-input";

const EDIT_VALUE_FORMAT = "YYYY-MM-DD";
const DISPLAY_VALUE_FORMAT = "MMM DD, YYYY";

interface Props {
  value: Moment | null;
  minDate?: Moment;
  maxDate?: Moment;
  placeholder?: string;
  className?: string;
  textInputClassName?: string;
  label?: string;
  onChange: (value: Moment) => void;
}

/* TODO: Transition component to wrap MaskedTextInput at the top level, providing props to control
   the masking, validation and display value. */
export const DateInput = ({
  value,
  minDate,
  maxDate,
  placeholder,
  className,
  textInputClassName,
  label,
  onChange,
}: Props) => {
  const [hasFocus, setHasFocus] = useState(false);
  const { ref, setValue: setEditValue } = useIMask<
    HTMLInputElement,
    { mask: string }
  >(
    { mask: "0000-00-00" },
    {
      onAccept: (val) => {
        setEditValue(val);

        if (!moment(val, EDIT_VALUE_FORMAT, true).isValid()) {
          return;
        }

        const date = moment.tz(val, EDIT_VALUE_FORMAT, moment.tz.guess());

        if (minDate && minDate.isAfter(date)) {
          return;
        }

        if (maxDate && maxDate.isBefore(date)) {
          return;
        }

        onChange(date);
      },
    },
  );

  useEffect(() => {
    setEditValue(value && hasFocus ? value.format(EDIT_VALUE_FORMAT) : "");
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [value, hasFocus]);

  const displayValue = value && value.format(DISPLAY_VALUE_FORMAT);

  return (
    <div className={clsx("relative", className)}>
      {label && <label className="mb-1 text-xs font-semibold">{label}</label>}
      <div className={textInputClassName}>
        {!hasFocus && (
          <span
            className={clsx(
              "position-absolute inline-block pointer-events-none h-8 leading-8 pl-3",
            )}
          >
            {displayValue}
          </span>
        )}
        <TextInput
          ref={ref}
          inputClassName="bg-transparent cursor-pointer"
          onFocus={() => {
            setEditValue(value ? value.format(EDIT_VALUE_FORMAT) : "");
            setHasFocus(true);
          }}
          onBlur={() => setHasFocus(false)}
          placeholder={hasFocus ? EDIT_VALUE_FORMAT : !value ? placeholder : ""}
          rightIcon={<i className="fa-regular fa-calendar" />}
        />
      </div>
    </div>
  );
};
